// In-game names ("handles"): one per player, unique across the game, reserved on the waitlist and kept when the game
// opens. The same rules on the site (as you type) and on the server (which decides). Unique ignoring case and look-alike
// characters: "Simran_09" blocks "simran_09" and "S1MRAN_09".

export const HANDLE_MIN = 3;
export const HANDLE_MAX = 16;

/** what a name is compared by: lower case, look-alike digits folded into letters, no separators */
export function handleKey(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[0]/g, 'o')
    .replace(/[1!|]/g, 'i')
    .replace(/[3]/g, 'e')
    .replace(/[4@]/g, 'a')
    .replace(/[5$]/g, 's')
    .replace(/[7]/g, 't')
    .replace(/[_.\-]/g, '');
}

// names that would pretend to be us, staff, the police or the system
const RESERVED = ['admin', 'administrator', 'mod', 'moderator', 'support', 'help', 'staff', 'official', 'system', 'server', 'ggbharat', 'challancity', 'police', 'traffic', 'cop', 'govt', 'government', 'guest', 'null', 'undefined', 'anonymous', 'everyone', 'owner', 'founder', 'dev', 'developer', 'bot'];
// abuse, folded the same way as the names (a short list: the obvious ones, English, Hindi and Punjabi): anywhere in
// the name, or (words that hide inside ordinary ones, like "grapes") only at its start
const BLOCKED = ['fuck', 'fuk', 'shit', 'bitch', 'cunt', 'pussy', 'whore', 'slut', 'nigg', 'porn', 'nazi', 'hitler', 'chutiy', 'chutia', 'madarchod', 'maderchod', 'bhenchod', 'behenchod', 'benchod', 'bhosd', 'gandu', 'randi', 'lavda', 'lauda', 'bsdk', 'harami', 'kamina'];
const BLOCKED_START = ['rape', 'sex', 'dick', 'lund', 'kutt', 'gaand', 'mc', 'bc'];

export type HandleCheck = { ok: true; value: string; key: string } | { ok: false; error: string };

/** A name as typed: trimmed, and checked against the rules (not against who has it: the server knows that). */
export function checkHandle(input: unknown): HandleCheck {
  if (typeof input !== 'string') return { ok: false, error: 'Pick a name.' };
  const value = input.trim();
  if (value.length < HANDLE_MIN) return { ok: false, error: `At least ${HANDLE_MIN} characters.` };
  if (value.length > HANDLE_MAX) return { ok: false, error: `At most ${HANDLE_MAX} characters.` };
  if (!/^[A-Za-z][A-Za-z0-9_.]*$/.test(value)) return { ok: false, error: 'Start with a letter; then letters, numbers, _ or . only.' };
  if (/[_.]{2}/.test(value) || /[_.]$/.test(value)) return { ok: false, error: 'No double or trailing _ or .' };
  const key = handleKey(value);
  if (RESERVED.includes(key)) return { ok: false, error: 'That name is reserved.' };
  // the two-letter ones only as (nearly) the whole name: "mc12" is out, "McQueen" is fine
  const starts = (w: string) => key.startsWith(w) && (w.length > 2 || key.length <= w.length + 2);
  if (BLOCKED.some((w) => key.includes(w)) || BLOCKED_START.some(starts)) return { ok: false, error: 'Pick a different name.' };
  return { ok: true, value, key };
}

/** An Indian mobile number (10 digits from 6–9, with or without +91 / 0), as +91XXXXXXXXXX; or another country's in +… form. */
export function checkPhone(input: unknown): { ok: true; value: string } | { ok: false; error: string } {
  if (typeof input !== 'string') return { ok: false, error: 'Enter your phone number.' };
  const raw = input.replace(/[\s()-]/g, '');
  const digits = raw.replace(/^\+/, '');
  if (/^(?:91|0)?[6-9]\d{9}$/.test(digits) && (!raw.startsWith('+') || raw.startsWith('+91'))) return { ok: true, value: `+91${digits.slice(-10)}` };
  if (raw.startsWith('+') && !raw.startsWith('+91') && /^\+[1-9]\d{7,14}$/.test(raw)) return { ok: true, value: raw };
  return { ok: false, error: 'Enter a 10-digit mobile number (or +country code for outside India).' };
}

/** India's states and union territories, for the location step */
export const STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Outside India',
];

export function checkPlace(state: unknown, city: unknown): { ok: true; state: string; city: string } | { ok: false; error: string } {
  if (typeof state !== 'string' || !STATES.includes(state)) return { ok: false, error: 'Pick your state.' };
  const c = typeof city === 'string' ? city.replace(/[^\p{L}\p{M} .,'-]/gu, '').replace(/\s+/g, ' ').trim() : '';
  if (c.length < 2 || c.length > 40) return { ok: false, error: 'Enter your city or town.' };
  return { ok: true, state, city: c };
}
